import React, { useCallback, useState, FC, ReactElement, useEffect } from 'react';
import { connect } from "react-redux";
import { compose } from "recompose";
import { withHttpClient } from "../HttpClient";
import { Typography, Divider, Grid } from "@material-ui/core";
import { Accordion, AccordionSummary, AccordionDetails } from '@material-ui/core';
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Avatar from "@material-ui/core/Avatar";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { ActiveFilter, FilterOption, Filters } from '../Filters';
import { FiltersPreview } from '../Filters/preview';
import { LeadClosingRatioReportFilters } from '../Filters/types';
import { toQuery } from '../../util/FilterUtil';
import DateFnsUtils from '@date-io/date-fns';
import ReportsApi from '../../api/Reports';
import _ from "lodash";
import moment from 'moment';

interface IOverallClosingRatio {
  displayFilter: boolean;
  httpClient: any;
  metadata: any;
  title: string;
}

export const OverallClosingRatio: FC<IOverallClosingRatio> = ({ displayFilter, httpClient, metadata, title }): ReactElement => {
  const reports = httpClient.reports as ReportsApi;
  const [expanded, setExpanded] = useState<boolean>(false);
  const [data, setData] = useState<any[]>([]);
  const [filters, setFilters] = useState<ActiveFilter[]>([]);
  const [filterOptions, setFilterOptions] = useState<FilterOption[]>(LeadClosingRatioReportFilters);

  const initFilterOptions = useCallback(() => {
    if (metadata?.sources) {
      const sourcesMap = new Map();
      metadata.sources.map((s: any) => sourcesMap.set(s.name, s.name));
      const sources = Array.from(sourcesMap).map(([key, value]) => ({ key, value }));
      const options = [...filterOptions];
      const sourceFilter = options.find(o => o.id === 'source');
      if (sourceFilter) {
        sourceFilter.options = sources;
        setFilterOptions(options);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadata]);

  const loadReports = useCallback(async () => {
    if (metadata?.users) {
      const queryParams = toQuery(filters);
      const apps = await loadAppsReport(queryParams);
      const leads = await loadLeadsReport(queryParams);
      setData(calculateOverallRatio(apps, leads));
      setExpanded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadata, filters]); 

  useEffect(() => {
    initFilterOptions();
  }, [initFilterOptions]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleExpand = () => {
    setExpanded(!expanded);
  }

  const handleApplyFilter = (filter: ActiveFilter) => {
    setFilters([...filters.filter(f => f.option.type !== 'dateRange'), filter]);
  }

  const handleDeleteFilter = (index: number) => {
    const _filters = [...filters];
    _filters.splice(index, 1);
    setFilters(_filters);
  }

  const loadLeadsReport = async (query: string) => query.length
      ? await reports.leadsByFilter(query)
      : await reports.leadsByFilter(getDefaultQuery());

  const loadAppsReport = async (query: string) => query.length
    ? await reports.appsByFilter(query)
    : await reports.appsByFilter(getDefaultQuery());

  const getDefaultQuery = () => {
      const from = moment(new Date()).startOf('month').format('YYYY-MM-DD');
      const to = moment(new Date()).endOf('month').format('YYYY-MM-DD');
      return `createdAt__between=${from},${to}`;
  }

  const isSentToApi = (status: string): boolean => {
    return status.toLowerCase() === "sent to api";
  }

  const calculateOverallRatio = (apps: any, leads: any): any => {
    const groupedApps = _.groupBy(apps, e => e.uid);
    let totalSubmitted = 0;
    let totalLeads = 0;

    leads.forEach((current: any) => {
      const user = current.uid ? metadata.users[current.uid] : { firstName: "Unknown", lastName: "", photoURL: "" };
      const app = groupedApps[user.id?.toString() || ''];
      if (!app) {
        totalLeads += current.count;
        return;
      }
      const submittedCount = app.reduce(
        (p, c) => {
          if (isSentToApi(c.status)) {
            p += c.count;
          }
          return p;
        },
        0
      );
      totalSubmitted += submittedCount;
      totalLeads += current.count;
    });

    const overallRatio = ((totalSubmitted / totalLeads) * 100).toFixed(2);

    return [{
      overallSubmitted: totalSubmitted,
      overallLeads: totalLeads,
      overallRatio
    }];
  }

  return (
    <Accordion expanded={expanded} onChange={handleExpand}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="overline">{title}</Typography>
      </AccordionSummary>
      {displayFilter &&
        <Grid
          container
          spacing={1}
          justifyContent="space-between"
          alignItems="center"
        >
          <Grid item>
            <FiltersPreview filters={filters} onDeleteFilter={handleDeleteFilter} />
          </Grid>
          <Grid item>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <Filters options={filterOptions} onApplyFilter={handleApplyFilter} />
            </MuiPickersUtilsProvider>
          </Grid>
        </Grid>
      }
      {displayFilter &&
        <Divider />
      }
      <AccordionDetails>
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>Total Leads</TableCell>
              <TableCell>Total Submitted</TableCell>
              <TableCell>Overall Closing Ratio</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data &&
              data.map((row, i) => (
                <TableRow key={i}>
                  <TableCell>{row.overallLeads}</TableCell>
                  <TableCell>{row.overallSubmitted}</TableCell>
                  <TableCell>{`${row.overallRatio}%`}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </AccordionDetails>
    </Accordion>
  );
}

export default compose<IOverallClosingRatio, Element>(
  withHttpClient,
  connect((state: any) => ({
    metadata: state.metadata
  }))
)(OverallClosingRatio);
